import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { ministriesService, type MinistryQueryParams, type Ministry } from '../../services/ministries.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { formatDate } from 'sharedUi/dateUtils';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'name', header: 'Ministry Name', filter: true }];

const MinistriesList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'ministries',
    basePath: '/ministries',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.MINISTRIES) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.MINISTRIES);
  const canDelete = useCanDelete(JOB_NAMES.MINISTRIES);

  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  const fetchMinistries = useCallback(
    async (
      page = 1,
      size = pageSize,
      sortBy: string | null = sortField,
      order: 1 | -1 | null = sortOrder,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);
        const params: MinistryQueryParams = {
          page,
          page_size: size,
          include_deleted: includeDeleted,
        };

        if (debouncedSearchText && debouncedSearchText.trim()) {
          params.search = debouncedSearchText.trim();
        }
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;

        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showCore;

        const response = await ministriesService.getAll(params);
        const mappedData = (response.data || []).map((item: any) => ({ ...item, id: item._id }));
        setMinistries(mappedData);

        const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
        setTotalRecords(total);
      } catch (err: any) {
        console.error('Failed to fetch ministries:', err);
        showError(extractErrorMessage(err, 'Failed to load ministries. Please try again.'));
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted, debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]
  );

  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchMinistries(newPage, event.rows, sortField, sortOrder);
  };

  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    fetchMinistries(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchMinistries(1, pageSize, sortField, sortOrder, next);
  };

  useEffect(() => {
    setFirst(0);
    fetchMinistries(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string) => navigateToView(id);
  const handleEdit = (id: string) => navigateToEdit(id);

  const handleDeleteClick = (id: string) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try {
      setDeleteLoading(true);
      await ministriesService.delete(selectedId);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      fetchMinistries(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (err: any) {
      console.error('Failed to delete ministry:', err);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      showError(extractErrorMessage(err, 'Failed to delete ministry'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const columns: SaasColumn<Ministry>[] = useMemo(() => {
    const cols: SaasColumn<Ministry>[] = [
      { field: 'name', header: 'Ministry Name', sortable: true, filter: true, hideable: false },
      { field: 'createdAt', header: 'Created At', sortable: true, body: (r) => formatDate(r.createdAt) },
    ];

    if (includeDeleted) {
      cols.push({
        field: 'status',
        header: 'Status',
        body: (r) => {
          const deleted = (r as any).isDelete || r.isDeleted;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    cols.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    cols.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (r) => {
        const deleted = (r as any).isDelete || r.isDeleted;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleView(r._id)}
              data-testid="MinistriesList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
            </button>
            {!deleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => handleEdit(r._id)}
                    data-testid="MinistriesList.Action.Edit"
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(r._id)}
                    data-testid="MinistriesList.Action.Delete"
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [canUpdate, canDelete, canManageProjects, includeDeleted, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.MINISTRIES}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-Ministries-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-building"
          title="Ministries"
          subtitle="Manage government ministries and their logos"
          testId="Ministries.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="ministries"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={ministries}
            loading={loading}
            dataKey="_id"
            emptyMessage="No ministries found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search ministries..."
            testId="MinistriesList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="MinistriesList.Filter.Project"
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="Ministries.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchMinistries(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                />
                {canCreate && (
                  <Button
                    label="New Ministry"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="MinistriesList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this ministry? This will mark it as deleted."
          testId="MinistriesList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default MinistriesList;
